import React from 'react';
import { Slide, Theme, AspectRatio } from '../types';
import { generatePicsumImage } from '../utils/imageSearch';
import { AnimatedBackground, VantaEffectType } from './AnimatedBackground';
import { SlideTheme, DividerType } from './SlideEditingPanel';

interface SlideBackgroundProps {
  slide: Slide;
  theme: Theme;
  aspectRatio: AspectRatio;
}

export const SlideBackground: React.FC<SlideBackgroundProps> = ({
  slide,
  theme,
  aspectRatio,
}) => {
  // 슬라이드 테마와 분할 타입 변수 선언 (상단으로 이동)
  const slideTheme = slide.slideTheme as SlideTheme;
  const dividerType = slide.dividerType as DividerType;

  // 중복된 contentBgColor 코드를 하나의 함수로 통합
  const getContentBackgroundColor = () => {
    return slide.contentBgColor || `linear-gradient(135deg, ${theme.quaternary}60, ${theme.accent}40)`;
  };
  
  // 배경 URL 계산
  const getBackgroundUrl = () => {
    if (slide.backgroundSeed) {
      return generatePicsumImage(
        aspectRatio.width,
        aspectRatio.height,
        slide.backgroundSeed,
        0, // blur는 CSS filter로 처리
        slide.backgroundGrayscale || false
      );
    }
    return slide.backgroundImage;
  };

  // 배경 스타일 계산
  const getBackgroundStyle = (): React.CSSProperties => {
    if (slide.backgroundType === 'color' && slide.backgroundColor) {
      return { background: slide.backgroundColor };
    } else if (slide.backgroundSeed || slide.backgroundImage) {
      const backgroundUrl = getBackgroundUrl();
      const themeHex = theme.primary.replace('#', '');
      const r = parseInt(themeHex.substr(0, 2), 16);
      const g = parseInt(themeHex.substr(2, 2), 16);
      const b = parseInt(themeHex.substr(4, 2), 16);
      const themeRgba = `rgba(${r}, ${g}, ${b}, ${slide.themeOverlay || 0.3})`;
      return {
        background: `linear-gradient(${themeRgba}, ${themeRgba}), url(${backgroundUrl}) center/cover`,
      };
    }
    return {
      background: `linear-gradient(135deg, ${theme.primary}15, ${theme.secondary}15)`,
    };
  };

  // 분할 디바이더의 SVG 패스 생성
  const getDividerPath = (dividerType: DividerType = 'straight', slideTheme: SlideTheme = 'left-split', splitPos = 25): string => {
    const isVertical = slideTheme === 'left-split' || slideTheme === 'right-split';
    const isHorizontal = slideTheme === 'top-split' || slideTheme === 'bottom-split';
    
    if (isVertical) {
      // 세로 분할 (왼쪽/오른쪽)
      const x = slideTheme === 'left-split' ? splitPos : 100 - splitPos;
      switch (dividerType) {
        case 'convex':
          return `M 0 0 L ${x} 0 Q ${x + 5} 50 ${x} 100 L 0 100 Z`;
        case 'concave':
          return `M 0 0 L ${x} 0 Q ${x - 5} 50 ${x} 100 L 0 100 Z`;
        case 'zigzag':
          return `M 0 0 L ${x} 0 L ${x - 2} 10 L ${x + 2} 20 L ${x - 2} 30 L ${x + 2} 40 L ${x - 2} 50 L ${x + 2} 60 L ${x - 2} 70 L ${x + 2} 80 L ${x - 2} 90 L ${x} 100 L 0 100 Z`;
        case 'cloud':
          return `M 0 0 L ${x} 0 Q ${x + 3} 10 ${x} 20 Q ${x - 3} 30 ${x} 40 Q ${x + 3} 50 ${x} 60 Q ${x - 3} 70 ${x} 80 Q ${x + 3} 90 ${x} 100 L 0 100 Z`;
        case 'straight':
        default:
          return `M 0 0 L ${x} 0 L ${x} 100 L 0 100 Z`;
      }
    } else if (isHorizontal) {
      // 가로 분할 (위/아래)
      const y = slideTheme === 'top-split' ? splitPos : 100 - splitPos;
      switch (dividerType) {
        case 'convex':
          return `M 0 0 L 100 0 L 100 ${y} Q 50 ${y + 5} 0 ${y} Z`;
        case 'concave':
          return `M 0 0 L 100 0 L 100 ${y} Q 50 ${y - 5} 0 ${y} Z`;
        case 'zigzag':
          return `M 0 0 L 100 0 L 100 ${y} L 90 ${y - 2} L 80 ${y + 2} L 70 ${y - 2} L 60 ${y + 2} L 50 ${y - 2} L 40 ${y + 2} L 30 ${y - 2} L 20 ${y + 2} L 10 ${y - 2} L 0 ${y} Z`;
        case 'cloud':
          return `M 0 0 L 100 0 L 100 ${y} Q 90 ${y + 3} 80 ${y} Q 70 ${y - 3} 60 ${y} Q 50 ${y + 3} 40 ${y} Q 30 ${y - 3} 20 ${y} Q 10 ${y + 3} 0 ${y} Z`;
        case 'straight':
        default:
          return `M 0 0 L 100 0 L 100 ${y} L 0 ${y} Z`;
      }
    }
    
    // 대각선 분할
    return `M 0 0 L 70 0 L 30 100 L 0 100 Z`;
  };

  // 필터 스타일 계산
  const getFilterStyle = () => {
    const filters = [];
    
    if (slide.backgroundPattern && slide.backgroundPattern !== 'none') {
      switch (slide.backgroundPattern) {
        case 'gaussian-blur':
          filters.push('blur(2px)');
          break;
        case 'motion-blur-horizontal':
        case 'motion-blur-vertical':
          filters.push('blur(1px)');
          break;
        case 'fisheye':
          filters.push('brightness(1.1) contrast(1.1)');
          break;
        case 'wide-angle':
          filters.push('brightness(0.9) contrast(1.2)');
          break;
      }
    } else if (slide.backgroundImage && slide.backgroundBlur) {
      filters.push(`blur(${slide.backgroundBlur}px)`);
    }
    
    return filters.length > 0 ? filters.join(' ') : 'none';
  };

  // 애니메이션 배경인 경우 - 다른 배경과 중첩되지 않도록 먼저 처리
  if (slide.backgroundType === 'animated' && slide.animatedBackgroundEffect) {
    // 슬라이드 테마가 적용된 경우
    if (slideTheme && slideTheme !== 'default') {
      const splitPos = slide.splitPosition || 25;
      const complementPos = 100 - splitPos;
      
      return (
        <>
          {/* 애니메이션 영역 (이미지 영역에만 적용) */}
          <div
            style={{
              position: 'absolute',
              zIndex: 1,
              clipPath: (() => {
                if (dividerType !== 'straight' && dividerType !== 'gradient' && slideTheme !== 'diagonal') {
                  const svgPath = getDividerPath(dividerType, slideTheme, splitPos);
                  return `path('${svgPath}')`;
                }
                
                if (slideTheme === 'left-split') {
                  return `polygon(0 0, ${splitPos}% 0, ${splitPos}% 100%, 0 100%)`;
                } else if (slideTheme === 'right-split') {
                  return `polygon(${complementPos}% 0, 100% 0, 100% 100%, ${complementPos}% 100%)`;
                } else if (slideTheme === 'top-split') {
                  return `polygon(0 0, 100% 0, 100% ${splitPos}%, 0 ${splitPos}%)`;
                } else if (slideTheme === 'bottom-split') {
                  return `polygon(0 ${complementPos}%, 100% ${complementPos}%, 100% 100%, 0 100%)`;
                } else if (slideTheme === 'diagonal') {
                  return `polygon(0 0, ${70 + (splitPos - 25)}% 0, ${30 + (splitPos - 25)}% 100%, 0 100%)`;
                }
                return 'none';
              })(),
              inset: 0,
              overflow: 'hidden',
            }}
          >
            <AnimatedBackground
              effect={slide.animatedBackgroundEffect as VantaEffectType}
              options={{
                ...slide.animatedBackgroundOptions,
                backgroundColor: slide.animatedBackgroundOptions?.backgroundColor || '#000000',
              }}
              className="slide-background-layer"
            />
          </div>

          {/* 콘텐츠 영역 배경 */}
          <div
            style={{
              position: 'absolute',
              zIndex: 2,
              clipPath: slideTheme === 'left-split' 
                ? `polygon(${splitPos}% 0, 100% 0, 100% 100%, ${splitPos}% 100%)`
                : slideTheme === 'right-split'
                ? `polygon(0 0, ${complementPos}% 0, ${complementPos}% 100%, 0 100%)`
                : slideTheme === 'top-split'
                ? `polygon(0 ${splitPos}%, 100% ${splitPos}%, 100% 100%, 0 100%)`
                : slideTheme === 'bottom-split'
                ? `polygon(0 0, 100% 0, 100% ${complementPos}%, 0 ${complementPos}%)`
                : slideTheme === 'diagonal'
                ? `polygon(${30 + (splitPos - 25)}% 100%, 100% 100%, 100% 0, ${70 + (splitPos - 25)}% 0)`
                : 'none',
              background: getContentBackgroundColor(),
              inset: 0,
            }}
          />
        </>
      );
    }
    
    // 기본 애니메이션 배경 (전체 화면)
    return (
      <>
        <AnimatedBackground
          effect={slide.animatedBackgroundEffect as VantaEffectType}
          options={{
            ...slide.animatedBackgroundOptions,
            backgroundColor: slide.animatedBackgroundOptions?.backgroundColor || '#000000',
          }}
          className="slide-background-layer"
        />
        {/* 필터 오버레이 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            filter: getFilterStyle(),
            pointerEvents: 'none',
          }}
        />
      </>
    );
  }

  // 애니메이션 배경이 아닌 경우에만 다른 배경 처리
  if (slide.backgroundType !== 'animated') {
    // 슬라이드 테마에 따른 분할 배경 렌더링

  // 색상 배경이 슬라이드 테마를 고려하는 경우
  if (slideTheme && slideTheme !== 'default' && slide.backgroundType === 'color' && slide.backgroundColor) {
    const splitPos = slide.splitPosition || 25;
    const complementPos = 100 - splitPos;
    
    return (
      <>
        {/* 기본 배경 */}
        <div
          className="slide-background-layer"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: `linear-gradient(135deg, ${theme.primary}15, ${theme.secondary}15)`,
          }}
        />
        
        {/* 색상 배경 (이미지 영역에만 적용) */}
        <div
          style={{
            position: 'absolute',
            zIndex: 1,
            clipPath: (() => {
              if (dividerType !== 'straight' && dividerType !== 'gradient' && slideTheme !== 'diagonal') {
                const svgPath = getDividerPath(dividerType, slideTheme, splitPos);
                return `path('${svgPath}')`;
              }
              
              if (slideTheme === 'left-split') {
                return `polygon(0 0, ${splitPos}% 0, ${splitPos}% 100%, 0 100%)`;
              } else if (slideTheme === 'right-split') {
                return `polygon(${complementPos}% 0, 100% 0, 100% 100%, ${complementPos}% 100%)`;
              } else if (slideTheme === 'top-split') {
                return `polygon(0 0, 100% 0, 100% ${splitPos}%, 0 ${splitPos}%)`;
              } else if (slideTheme === 'bottom-split') {
                return `polygon(0 ${complementPos}%, 100% ${complementPos}%, 100% 100%, 0 100%)`;
              } else if (slideTheme === 'diagonal') {
                return `polygon(0 0, ${70 + (splitPos - 25)}% 0, ${30 + (splitPos - 25)}% 100%, 0 100%)`;
              }
              return 'none';
            })(),
            background: slide.backgroundColor,
            inset: 0,
          }}
        />

        {/* 콘텐츠 영역 배경 */}
        <div
          style={{
            position: 'absolute',
            zIndex: 2,
            clipPath: slideTheme === 'left-split' 
              ? `polygon(${splitPos}% 0, 100% 0, 100% 100%, ${splitPos}% 100%)`
              : slideTheme === 'right-split'
              ? `polygon(0 0, ${complementPos}% 0, ${complementPos}% 100%, 0 100%)`
              : slideTheme === 'top-split'
              ? `polygon(0 ${splitPos}%, 100% ${splitPos}%, 100% 100%, 0 100%)`
              : slideTheme === 'bottom-split'
              ? `polygon(0 0, 100% 0, 100% ${complementPos}%, 0 ${complementPos}%)`
              : slideTheme === 'diagonal'
              ? `polygon(${30 + (splitPos - 25)}% 100%, 100% 100%, 100% 0, ${70 + (splitPos - 25)}% 0)`
              : 'none',
            background: getContentBackgroundColor(),
            inset: 0,
          }}
        />
      </>
    );
  }

  if (slideTheme && slideTheme !== 'default' && slide.backgroundType !== 'color' && slide.backgroundType !== 'animated') {
    const backgroundUrl = getBackgroundUrl();
    const themeHex = theme.primary.replace('#', '');
    const r = parseInt(themeHex.substr(0, 2), 16);
    const g = parseInt(themeHex.substr(2, 2), 16);
    const b = parseInt(themeHex.substr(4, 2), 16);
    const themeRgba = `rgba(${r}, ${g}, ${b}, ${slide.themeOverlay || 0.3})`;

    // 동적 분할 위치 (기본값: 25%)
    const splitPos = slide.splitPosition || 25;
    const complementPos = 100 - splitPos;

    // 분할된 배경 렌더링
    return (
      <>
        {/* 기본 배경 */}
        <div
          className="slide-background-layer"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: `linear-gradient(135deg, ${theme.primary}15, ${theme.secondary}15)`,
          }}
        />
        
        {/* 이미지 영역 */}
        <div
          style={{
            position: 'absolute',
            zIndex: 1,
            clipPath: (() => {
              // SVG 경로를 사용한 곡선 경계
              if (dividerType !== 'straight' && dividerType !== 'gradient' && slideTheme !== 'diagonal') {
                const svgPath = getDividerPath(dividerType, slideTheme, splitPos);
                return `path('${svgPath}')`;
              }
              
              // 기존 직선 분할
              if (slideTheme === 'left-split') {
                return `polygon(0 0, ${splitPos}% 0, ${splitPos}% 100%, 0 100%)`;
              } else if (slideTheme === 'right-split') {
                return `polygon(${complementPos}% 0, 100% 0, 100% 100%, ${complementPos}% 100%)`;
              } else if (slideTheme === 'top-split') {
                return `polygon(0 0, 100% 0, 100% ${splitPos}%, 0 ${splitPos}%)`;
              } else if (slideTheme === 'bottom-split') {
                return `polygon(0 ${complementPos}%, 100% ${complementPos}%, 100% 100%, 0 100%)`;
              } else if (slideTheme === 'diagonal') {
                if (dividerType === 'gradient') {
                  return `polygon(0 0, 100% 0, 100% 100%, 0 100%)`;
                }
                return `polygon(0 0, ${70 + (splitPos - 25)}% 0, ${30 + (splitPos - 25)}% 100%, 0 100%)`;
              }
              return 'none';
            })(),
            background: backgroundUrl 
              ? `linear-gradient(${themeRgba}, ${themeRgba}), url(${backgroundUrl}) center/cover`
              : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
            filter: getFilterStyle(),
            inset: 0,
          }}
        />

        {/* 콘텐츠 영역 배경 */}
        <div
          style={{
            position: 'absolute',
            zIndex: 2,
            clipPath: slideTheme === 'left-split' 
              ? `polygon(${splitPos}% 0, 100% 0, 100% 100%, ${splitPos}% 100%)`
              : slideTheme === 'right-split'
              ? `polygon(0 0, ${complementPos}% 0, ${complementPos}% 100%, 0 100%)`
              : slideTheme === 'top-split'
              ? `polygon(0 ${splitPos}%, 100% ${splitPos}%, 100% 100%, 0 100%)`
              : slideTheme === 'bottom-split'
              ? `polygon(0 0, 100% 0, 100% ${complementPos}%, 0 ${complementPos}%)`
              : slideTheme === 'diagonal'
              ? dividerType === 'gradient'
                ? `linear-gradient(45deg, ${theme.quaternary}40 48%, transparent 50%, transparent 52%)`
                : `polygon(${30 + (splitPos - 25)}% 100%, 100% 100%, 100% 0, ${70 + (splitPos - 25)}% 0)`
              : 'none',
            background: getContentBackgroundColor(),
            inset: 0,
          }}
        />

        {/* 분할 유형별 디바이더 - 모든 테마에 적용 */}
        {dividerType && dividerType !== 'straight' && slideTheme !== 'default' && (
          <>
            <svg width="0" height="0" style={{ position: 'absolute' }}>
              <defs>
                <clipPath id={`divider-${dividerType}-image`}>
                  <path d={getDividerPath(dividerType, 100, 100)} 
                        vectorEffect="non-scaling-stroke" />
                </clipPath>
                <clipPath id={`divider-${dividerType}-content`}>
                  <path d={(() => {
                    // 콘텐츠 영역을 위한 역방향 클립패스
                    switch (slideTheme) {
                      case 'left-split':
                        return getDividerPath(dividerType, complementPos, 100).replace('M 0 0', `M ${splitPos} 0`);
                      case 'right-split':
                        return getDividerPath(dividerType, complementPos, 100);
                      case 'top-split':
                        return getDividerPath(dividerType, 100, complementPos).replace('M 0 0', `M 0 ${splitPos}`);
                      case 'bottom-split':
                        return getDividerPath(dividerType, 100, complementPos);
                      case 'diagonal':
                        return getDividerPath(dividerType, 70 + (splitPos - 25), 100).replace('M 0 0', `M ${30 + (splitPos - 25)} 0`);
                      default:
                        return getDividerPath(dividerType, 100, 100);
                    }
                  })()} 
                        vectorEffect="non-scaling-stroke" />
                </clipPath>
              </defs>
            </svg>
            
            {/* 이미지 영역에 분할 유형 적용 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 3,
                clipPath: `url(#divider-${dividerType}-image)`,
                background: backgroundUrl 
                  ? `linear-gradient(${themeRgba}, ${themeRgba}), url(${backgroundUrl}) center/cover`
                  : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                filter: getFilterStyle(),
              }}
            />
            
            {/* 콘텐츠 영역에 분할 유형 적용 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 3,
                clipPath: `url(#divider-${dividerType}-content)`,
                background: getContentBackgroundColor(),
              }}
            />
          </>
        )}
      </>
    );
  }

  // 기본 테마 렌더링
  return (
    <div
      className="slide-background-layer"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        ...getBackgroundStyle(),
        filter: getFilterStyle(),
      }}
    />
  );
  }
};